import * as React from "react";
import { Link } from "@tanstack/react-router";
import { Play, Pause, RefreshCw, Database, Clock, Trash2 } from "lucide-react";
import { Text } from "../../../ui/components/Text";
import { Button } from "../../../ui/components/Button";
import { Callout } from "../../../ui/components/Callout";
import { CollapsibleSection } from "../../../ui/components/CollapsibleSection";
import { DetailPageLayout } from "../layouts/DetailPageLayout";
import { useConfirmModal } from "../../hooks/useConfirmModal";
import { useSectionCollapse } from "../../hooks/useSectionCollapse";
import { useAuth } from "../../lib/auth/hooks";
import { useI18n } from "../../lib/i18n";
import { fetchSimulationStats, clearProductSimulationHistory } from "../../lib/api/simulation";
import type { SimulationStats } from "../../lib/api/simulation";
import * as layout from "../organizations/OrganizationEditPage.css";

function formatRetention(ms: number): string {
  const hours = ms / 3_600_000;
  if (hours < 24) return `${hours}h`;
  const days = hours / 24;
  return `${days}d`;
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function StatBadge({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        fontSize: 12,
        fontWeight: 600,
        padding: "2px 10px",
        borderRadius: 9999,
        background: "var(--color-muted, #F3F4F6)",
        color: "var(--color-fg, #111827)",
        border: "1px solid var(--color-border, #E5E7EB)",
        whiteSpace: "nowrap",
        lineHeight: 1.4,
        maxHeight: 24,
        boxSizing: "border-box",
      }}
    >
      <span style={{ fontWeight: 500, color: "var(--color-fgSecondary, #6B7280)" }}>
        {label}
      </span>
      <span style={{ fontWeight: 700 }}>{value}</span>
    </span>
  );
}

export function ProductSimulationPage() {
  const { accessToken } = useAuth();
  const { t } = useI18n();
  const { isExpanded, toggle, bindSectionLayout } = useSectionCollapse("product-simulation");
  const token = accessToken ?? localStorage.getItem("access_token");
  const { confirm, ConfirmModal } = useConfirmModal();

  const [stats, setStats] = React.useState<SimulationStats | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [refreshing, setRefreshing] = React.useState(false);
  const [clearingId, setClearingId] = React.useState<string | null>(null);

  const load = React.useCallback(async (isRefresh = false) => {
    if (!token) return;
    if (isRefresh) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const data = await fetchSimulationStats(token);
      setStats(data);
    } catch (err: any) {
      setError(err?.message || t("pro.system.simulation.error.load"));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token, t]);

  React.useEffect(() => { load(); }, [load]);

  const handleClearHistory = React.useCallback(async (productId: string, name: string) => {
    if (!token) return;
    const ok = await confirm(
      t("pro.system.simulation.confirm.clearTitle", { name }),
      t("pro.system.simulation.confirm.clearBody"),
      t("pro.system.simulation.confirm.clear"),
    );
    if (!ok) return;
    setClearingId(productId);
    try {
      await clearProductSimulationHistory(token, productId);
      setStats((prev) => prev ? {
        ...prev,
        pausedSessions: prev.pausedSessions.map((s) =>
          s.productId === productId ? { ...s, historyRowCount: 0 } : s
        ),
      } : prev);
    } catch {
      // silently fail — user can refresh to see current state
    } finally {
      setClearingId(null);
    }
  }, [token, confirm, t]);

  const sectionLayout = bindSectionLayout([
          { id: "active-sessions", label: t("pro.system.simulation.active.title"), icon: <Play size={14} /> },
          { id: "paused-sessions", label: t("pro.system.simulation.paused.title"), icon: <Pause size={14} /> },
        ]);

  return (
    <>
      <DetailPageLayout
        header={{
          media: <Database size={28} color="#9CA3AF" />,
          title: t("pro.system.simulation.title"),
          subtitle: t("pro.system.simulation.subtitle"),
          badges: stats
            ? [
                <StatBadge
                  key="total"
                  label={t("pro.system.simulation.stat.total")}
                  value={stats.totalSimulated}
                />,
                <StatBadge
                  key="active"
                  label={t("pro.system.simulation.stat.active")}
                  value={stats.activelySimulating}
                />,
                <StatBadge
                  key="rows"
                  label={t("pro.system.simulation.stat.historyRows")}
                  value={stats.historyRowCount.toLocaleString()}
                />,
                <StatBadge
                  key="retention"
                  label={t("pro.system.simulation.stat.retention")}
                  value={formatRetention(stats.retentionMs)}
                />,
              ]
            : undefined,
        }}
        contextActions={[
          {
            id: "refresh",
            label: t("pro.system.simulation.refresh"),
            icon: <RefreshCw size={16} />,
            color: "primary",
            onClick: () => load(true),
          },
        ]}
        {...sectionLayout}
      >
        {error && (
          <div style={{ marginBottom: 16 }}>
            <Callout variant="error">{error}</Callout>
          </div>
        )}

        {loading && !stats && (
          <Text level="body-medium" style={{ color: "var(--color-fgMuted)" }}>
            {t("shared.common.loading")}
          </Text>
        )}

        {stats && (
          <>
            {/* Active sessions */}
            <CollapsibleSection
              id="active-sessions"
              title={t("pro.system.simulation.active.title")}
              icon={<Play size={20} />}
              expanded={isExpanded("active-sessions")}
              onToggleExpanded={() => toggle("active-sessions")}
              statusIndicators={[
                <span
                  key="running"
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    padding: "1px 8px",
                    borderRadius: 9999,
                    background: "#F0FDF4",
                    color: "#15803D",
                    border: "1px solid #BBF7D0",
                  }}
                >
                  {t("pro.system.simulation.active.running", { count: stats.activelySimulating })}
                </span>,
              ]}
            >
              <div className={layout.cardInner}>
                {stats.activeSessions.length === 0 ? (
                  <Text level="body-small" style={{ color: "var(--color-fgMuted)" }}>
                    {t("pro.system.simulation.active.empty")}
                  </Text>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 160px 160px",
                        gap: 12,
                        padding: "6px 12px",
                        fontSize: 11,
                        fontWeight: 600,
                        color: "var(--color-fgSecondary)",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                      }}
                    >
                      <span>{t("pro.system.simulation.col.product")}</span>
                      <span>{t("pro.system.simulation.col.sessionStarted")}</span>
                      <span>{t("pro.system.simulation.col.expires")}</span>
                    </div>
                    {stats.activeSessions.map((session) => (
                      <Link
                        key={session.productId}
                        to="/workspace/products/$productId"
                        params={{ productId: session.productId }}
                        style={{ textDecoration: "none" }}
                      >
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 160px 160px",
                            gap: 12,
                            padding: "10px 12px",
                            borderRadius: 8,
                            cursor: "pointer",
                            transition: "background 120ms",
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.background = "var(--color-interactiveHover)")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.background = "transparent")
                          }
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <Play size={13} style={{ color: "#15803D", flexShrink: 0 }} />
                            <span
                              style={{
                                fontWeight: 500,
                                fontSize: 14,
                                color: "var(--color-fg)",
                              }}
                            >
                              {session.name}
                            </span>
                          </div>
                          <span
                            style={{
                              fontSize: 13,
                              color: "var(--color-fgSecondary)",
                              fontVariantNumeric: "tabular-nums",
                            }}
                          >
                            {formatDate(session.sessionStartedAt)}
                          </span>
                          <span
                            style={{
                              fontSize: 13,
                              color: "var(--color-fgSecondary)",
                              fontVariantNumeric: "tabular-nums",
                            }}
                          >
                            {formatDate(session.simulationExpAt)}
                          </span>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </CollapsibleSection>

            {/* Paused simulations */}
            <CollapsibleSection
              id="paused-sessions"
              title={t("pro.system.simulation.paused.title")}
              icon={<Pause size={20} />}
              expanded={isExpanded("paused-sessions")}
              onToggleExpanded={() => toggle("paused-sessions")}
              statusIndicators={[
                <span
                  key="paused"
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    padding: "1px 8px",
                    borderRadius: 9999,
                    background: "#EFF6FF",
                    color: "#1D4ED8",
                    border: "1px solid #BFDBFE",
                  }}
                >
                  {t("pro.system.simulation.paused.count", { count: stats.pausedSessions.length })}
                </span>,
              ]}
            >
              <div className={layout.cardInner}>
                {stats.pausedSessions.length === 0 ? (
                  <Text level="body-small" style={{ color: "var(--color-fgMuted)" }}>
                    {t("pro.system.simulation.paused.empty")}
                  </Text>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 140px 160px",
                        gap: 12,
                        padding: "6px 12px",
                        fontSize: 11,
                        fontWeight: 600,
                        color: "var(--color-fgSecondary)",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                      }}
                    >
                      <span>{t("pro.system.simulation.col.product")}</span>
                      <span>{t("pro.system.simulation.stat.historyRows")}</span>
                      <span />
                    </div>
                    {stats.pausedSessions.map((session) => (
                      <div
                        key={session.productId}
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 140px 160px",
                          gap: 12,
                          padding: "6px 12px",
                          borderRadius: 8,
                          alignItems: "center",
                        }}
                      >
                        <Link
                          to="/workspace/products/$productId"
                          params={{ productId: session.productId }}
                          style={{
                            textDecoration: "none",
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          <Pause size={13} style={{ color: "#1D4ED8", flexShrink: 0 }} />
                          <span
                            style={{
                              fontWeight: 500,
                              fontSize: 14,
                              color: "var(--color-fg)",
                            }}
                          >
                            {session.name}
                          </span>
                        </Link>
                        <span
                          style={{
                            fontSize: 13,
                            color: "var(--color-fgSecondary)",
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {session.historyRowCount.toLocaleString()}
                        </span>
                        <div style={{ display: "flex", justifyContent: "flex-end" }}>
                          <Button
                            type="button"
                            color="danger"
                            variant="ghost"
                            iconLeft={<Trash2 size={13} />}
                            loading={clearingId === session.productId}
                            disabled={clearingId !== null || session.historyRowCount === 0}
                            onClick={() =>
                              handleClearHistory(session.productId, session.name)
                            }
                            style={{ fontSize: 12, padding: "4px 10px", height: 28 }}
                          >
                            {t("pro.system.simulation.clearHistory")}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CollapsibleSection>
          </>
        )}
      </DetailPageLayout>
      <ConfirmModal />
    </>
  );
}
